import { LINK_CLASS, StatusChip } from '@league/ui';
import { Link, useOutletContext } from 'react-router-dom';

import { PrintLeagueTableTents, PrintNameCardsForEveryTable } from '../../seating/PrintTableNumbers';
import { leagueDate } from '../../../lib/when';
import { seatingState } from '../session-state';
import type { LeagueContext } from '../LeagueWorkspace';

/**
 * A list of sessions, each linking to its board.
 *
 * Not a placeholder: the board itself is built and lives at `/director/sessions/:id/seating`. What
 * was missing was a way to reach it that did not involve the old league page's session list, which
 * the tabs replaced — so this tab is the index the board always needed.
 */
export function SeatingTab() {
  const { league, sessions } = useOutletContext<LeagueContext>();

  return (
    <section className="flex flex-col gap-3">
      {/* The league's tents, placed as the Scores tab places its score sheet (2026-10-16). */}
      <div className="flex flex-wrap justify-end gap-2">
        <PrintLeagueTableTents leagueId={league.id} />
        {league.enrolledCount > 0 ? <PrintNameCardsForEveryTable leagueId={league.id} /> : null}
      </div>

      {sessions.length === 0 ? (
        /*
          A league always gets its sessions when it is created, so this should not happen — which is
          exactly why it is here. An empty bordered box reads as something broken; a sentence reads
          as a state, and points at the one screen that can fix it.
        */
        <p className="rounded-md border border-dashed border-light px-5 py-10 text-center text-[14px] text-ink-3">
          No sessions are scheduled for this league.
        </p>
      ) : (
        <ol className="flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
          {sessions.map((session) => (
            <li key={session.id} className="flex items-center justify-between gap-4 px-5 py-4">
              <div className="flex items-baseline gap-3">
                <span className="tabular text-[13px] text-ink-3">{session.sessionNumber}</span>
                <span className="font-medium">
                  {leagueDate(session.startsAt, league.timeZone, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <StatusChip status={seatingState(session)} />
                <Link
                  to={`/director/sessions/${session.id}/seating`}
                  className={`text-[13px] ${LINK_CLASS}`}
                >
                  Open the board
                </Link>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
